import {
  Component,
  Inject,
  viewChild,
  viewChildren,
  ElementRef,
  ChangeDetectionStrategy,
  computed,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ActionTypes,
  AddOwnerActions,
  DeleteAgentAction,
  DeletePowerBIResourceActions,
  IsolateResourceActions,
  RemoveAccessActions,
  RemovePermissionsActions,
  SetAllUsersAccessWorkspaceActions,
} from 'src/app/models/api/playbooks/actions/actionTypes.enum';
import { PlaybookActionDefinitionPropertiesBase } from 'src/app/models/api/playbooks/playbook.model';
import {
  PlaybooksActionDefinitionInput,
  PlaybooksActionInput,
  PlaybookSupportedActions,
} from 'src/app/models/componentInputs/playbookActionInput.interface';
import { PlaybooksActionOutput } from 'src/app/models/popups/outputs/playbookActionOutput.interface';
import { buildPlaybookActionTypeDisplayName } from '../../../labels/utils';
import { BehaviorSubject } from 'rxjs';
import { signal } from '@angular/core';
import { SetActionStatusActionComponent } from './actions/set-action-status-action/set-action-status-action.component';
import { SetAuthenticationModeComponent } from './actions/set-authentication-mode/set-authentication-mode.component';
import { SetConfirmationModeComponent } from './actions/set-confirmation-mode/set-confirmation-mode.component';
import {
  ActionStatus,
  PowerplatformAgentActionAuthenticationMode,
  PowerplatformAgentActionConfirmationMode,
  PowerplatformAgentActionSetAuthenticationModePayload,
  PowerplatformAgentActionSetConfirmationModePayload,
  PowerplatformAgentActionSetWebBrowsingPayload,
  PowerplatformEnvironmentSetAuditPayload,
} from 'src/app/services/httpServices/actionsHttpService/actions-http-service.service';
import { SetWebBrowsingActionComponent } from './actions/set-web-browsing-action/set-web-browsing-action.component';
import { SetEnvironmentAuditActionComponent } from './actions/set-environment-audit-action/set-environment-audit-action.component';
import { Labels } from 'src/app/labels/englishLabels';

interface ActionOptions {
  name: string;
  type: ActionTypes;
  tooltip?: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-playbook-action',
  templateUrl: './playbook-action.component.html',
  styleUrls: ['./playbook-action.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PlaybookActionComponent {
  public playbookAction?: ActionTypes;
  public selectedAction?: ActionTypes;

  public actionTypes = ActionTypes;

  public fg = new UntypedFormGroup({});
  public actionOptions = signal<ActionOptions[]>([]);

  public actionProperties?: PlaybookActionDefinitionPropertiesBase;
  public actionFormInput: PlaybooksActionDefinitionInput;
  public limitedActionText?: string;
  private readonly _supportedActions: PlaybookSupportedActions[];

  public showAddOwnerAction$ = new BehaviorSubject<boolean>(false);
  public showRemovePermissionsAction$ = new BehaviorSubject<boolean>(false);
  public showIsolateResourceAction$ = new BehaviorSubject<boolean>(false);
  public showRemoveAccessAction$ = new BehaviorSubject<boolean>(false);
  public showDeleteAgentAction$ = new BehaviorSubject<boolean>(false);
  public showDeletePowerBIResourceAction$ = new BehaviorSubject<boolean>(false);
  public showSetAllUsersAccessWorkspaceAction$ = new BehaviorSubject<boolean>(false);

  public setActionStatusActionComponent = viewChild(SetActionStatusActionComponent);
  public setAuthenticationModeActionComponent = viewChild(SetAuthenticationModeComponent);
  public setConfirmationModeActionComponent = viewChild(SetConfirmationModeComponent);
  public setWebBrowsingActionComponent = viewChild(SetWebBrowsingActionComponent);
  public setEnvironmentAuditActionComponent = viewChild(SetEnvironmentAuditActionComponent);

  readonly actionTitleRefs = viewChildren<ElementRef>('actionTitleRef');

  readonly truncationMap = computed(() => {
    const titleRefs = this.actionTitleRefs();
    const options = this.actionOptions();
    const map = new Map<string, boolean>();

    titleRefs.forEach((titleRef, index) => {
      if (titleRef.nativeElement && options[index]) {
        const element = titleRef.nativeElement;
        const actionType = options[index].type;
        const isTruncated = element.scrollWidth > element.clientWidth;
        map.set(actionType.toString(), isTruncated);
      }
    });

    return map;
  });

  public actionStatus = signal<ActionStatus>(
    (this.actionInput.action?.actionType
      ? this.actionInput.action?.actionType === ActionTypes.enableAgentAction
        ? ActionStatus.enabled
        : ActionStatus.disabled
      : ActionStatus.enabled) ?? ActionStatus.enabled
  );
  public authenticationMode = signal<PowerplatformAgentActionAuthenticationMode>(
    this.actionInput.action?.actionType === ActionTypes.setAgentActionAuthenticationMode
      ? ((this.actionInput.action?.actionDefinitionProperties as PowerplatformAgentActionSetAuthenticationModePayload)
          ?.authenticationMode ?? PowerplatformAgentActionAuthenticationMode.invoker)
      : PowerplatformAgentActionAuthenticationMode.invoker
  );
  public confirmationMode = signal<PowerplatformAgentActionConfirmationMode>(
    this.actionInput.action?.actionType === ActionTypes.setAgentActionConfirmationMode
      ? ((this.actionInput.action?.actionDefinitionProperties as PowerplatformAgentActionSetConfirmationModePayload)
          ?.confirmationMode ?? PowerplatformAgentActionConfirmationMode.strict)
      : PowerplatformAgentActionConfirmationMode.strict
  );
  public ignoreIfAutonomousWithTriggersAuth = signal<boolean>(
    this.actionInput.action?.actionType === ActionTypes.setAgentActionAuthenticationMode
      ? ((this.actionInput.action?.actionDefinitionProperties as PowerplatformAgentActionSetAuthenticationModePayload)
          ?.ignoreIfAutonomousWithTriggers ?? false)
      : false
  );
  public ignoreIfAutonomousWithTriggersConf = signal<boolean>(
    this.actionInput.action?.actionType === ActionTypes.setAgentActionConfirmationMode
      ? ((this.actionInput.action?.actionDefinitionProperties as PowerplatformAgentActionSetConfirmationModePayload)
          ?.ignoreIfAutonomousWithTriggers ?? false)
      : false
  );
  public webBrowsingMode = signal<boolean>(
    (this.actionInput.action?.actionDefinitionProperties as PowerplatformAgentActionSetWebBrowsingPayload)
      ?.isWebBrowsingEnabled ?? true
  );
  public auditMode = signal<boolean>(
    (this.actionInput.action?.actionDefinitionProperties as PowerplatformEnvironmentSetAuditPayload)?.isAuditEnabled ??
      true
  );

  constructor(
    public dialogRef: MatDialogRef<PlaybookActionComponent>,
    @Inject(MAT_DIALOG_DATA) public actionInput: PlaybooksActionInput
  ) {
    this._supportedActions = actionInput.supportedActions;
    this.limitedActionText = actionInput.limitedActionsTooltip;
    this._createActionOptions();

    this.dialogRef.disableClose = true;
    this.dialogRef.backdropClick().subscribe(() => {
      this.closeWindow();
    });

    actionInput.action?.let((action) => {
      if ([ActionTypes.enableAgentAction, ActionTypes.disableAgentAction].includes(action.actionType)) {
        this.selectedAction = ActionTypes.setActionStatus;
        this.playbookAction = ActionTypes.setActionStatus;
        this.actionProperties = action.actionDefinitionProperties;

        this.actionFormInput = {
          platform: this.actionInput.platform,
          actionType: action.actionType,
          actionDefinitionProperties: action.actionDefinitionProperties,
        } as PlaybooksActionDefinitionInput;
      } else if (
        DeleteAgentAction.includes(action.actionType) ||
        DeletePowerBIResourceActions.includes(action.actionType)
      ) {
        // For delete actions: treat as a new (empty) action so the Create button is shown
        // Keep the selection to render the correct form but clear playbookAction to switch button text
        this.playbookAction = undefined;
        this.actionProperties = undefined;
        this.selectedAction = action.actionType;

        // Ensure the form is empty
        this.fg = new UntypedFormGroup({});

        // If options are already initialized, select the delete action explicitly
        const deleteOption = this.actionOptions().find((o) => o.type === action.actionType);
        if (deleteOption) {
          this.onActionSelected(deleteOption);
        }
      } else {
        this.selectedAction = action.actionType;
        this.playbookAction = action.actionType;
        this.actionProperties = action.actionDefinitionProperties;

        this.actionFormInput = {
          platform: this.actionInput.platform,
          actionType: action.actionType,
          actionDefinitionProperties: action.actionDefinitionProperties,
        } as PlaybooksActionDefinitionInput;
      }
    });

    this.updateActionVisibility(actionInput.action?.actionType);
  }

  public isTruncated(actionType: ActionTypes): boolean {
    return this.truncationMap().get(actionType.toString()) ?? false;
  }

  public onActionSelected(action: ActionOptions) {
    this.fg = new UntypedFormGroup({});
    this._resetActionOnChange();

    if (action.disabled) return;

    this.actionFormInput = {
      platform: this.actionInput.platform,
      actionType: action.type,
      actionDefinitionProperties: this.actionProperties,
    } as PlaybooksActionDefinitionInput;

    this.selectedAction = action.type;
    this.updateActionVisibility(action.type);
  }

  public closeWindow() {
    this.dialogRef.close({} as PlaybooksActionOutput);
  }

  public createOrUpdate() {
    let selectedValue = null;
    let selectedAction = this.selectedAction;

    if (this.setActionStatusActionComponent()) {
      const statusComponent = this.setActionStatusActionComponent();
      if (statusComponent) {
        selectedValue = statusComponent.actionStatus();
      }

      if (this.selectedAction === ActionTypes.setActionStatus) {
        selectedAction =
          selectedValue === ActionStatus.enabled ? ActionTypes.enableAgentAction : ActionTypes.disableAgentAction;
      }

      this.fg.addControl('actionStatus', new UntypedFormControl(selectedValue));
    } else if (this.setAuthenticationModeActionComponent()) {
      const authComponent = this.setAuthenticationModeActionComponent();
      if (authComponent) {
        selectedValue = authComponent.selectedAuthenticationMode();
      }

      this.fg.addControl('authenticationMode', new UntypedFormControl(selectedValue));

      if (authComponent) {
        this.fg.addControl(
          'ignoreIfAutonomousWithTriggers',
          new UntypedFormControl(authComponent.ignoreIfAutonomousWithTriggers())
        );
      }
    } else if (this.setConfirmationModeActionComponent()) {
      const confirmComponent = this.setConfirmationModeActionComponent();
      if (confirmComponent) {
        selectedValue = confirmComponent.selectedConfirmationMode();
      }

      this.fg.addControl('confirmationMode', new UntypedFormControl(selectedValue));

      if (confirmComponent) {
        this.fg.addControl(
          'ignoreIfAutonomousWithTriggers',
          new UntypedFormControl(confirmComponent.ignoreIfAutonomousWithTriggers())
        );
      }
    } else if (this.setWebBrowsingActionComponent()) {
      const webBrowsingComponent = this.setWebBrowsingActionComponent();
      if (webBrowsingComponent) {
        selectedValue = webBrowsingComponent.webBrowsingMode();
      }
      this.fg.addControl('isWebBrowsingEnabled', new UntypedFormControl(selectedValue));
    } else if (this.setEnvironmentAuditActionComponent()) {
      const auditComponent = this.setEnvironmentAuditActionComponent();
      if (auditComponent) {
        selectedValue = auditComponent.auditMode();
      }
      this.fg.addControl('isAuditEnabled', new UntypedFormControl(selectedValue));
    }

    this.dialogRef.close({
      action: {
        actionType: selectedAction,
        actionDefinitionProperties:
          this.selectedAction !== ActionTypes.setActionStatus
            ? (this.fg.value as PlaybookActionDefinitionPropertiesBase)
            : {},
      },
    } as PlaybooksActionOutput);
  }

  private updateActionVisibility(action?: ActionTypes) {
    const isAddOwnerActionVisible = action != undefined && AddOwnerActions.includes(action);
    const isRemovePermissionsVisible = action != undefined && RemovePermissionsActions.includes(action);
    const isIsolateResourceActionVisible = action != undefined && IsolateResourceActions.includes(action);
    const isRemoveAccessActionVisible = action != undefined && RemoveAccessActions.includes(action);
    const isDeleteAgentActionVisible = action != undefined && DeleteAgentAction.includes(action);
    const isDeletePowerBIResourceActionVisible = action != undefined && DeletePowerBIResourceActions.includes(action);
    const isSetAllUsersAccessWorkspaceActionVisible =
      action != undefined && SetAllUsersAccessWorkspaceActions.includes(action);

    this.showAddOwnerAction$.next(isAddOwnerActionVisible);
    this.showRemovePermissionsAction$.next(isRemovePermissionsVisible);
    this.showIsolateResourceAction$.next(isIsolateResourceActionVisible);
    this.showRemoveAccessAction$.next(isRemoveAccessActionVisible);
    this.showDeleteAgentAction$.next(isDeleteAgentActionVisible);
    this.showDeletePowerBIResourceAction$.next(isDeletePowerBIResourceActionVisible);
    this.showSetAllUsersAccessWorkspaceAction$.next(isSetAllUsersAccessWorkspaceActionVisible);
  }

  private _createActionOptions() {
    let actionOptions = this._supportedActions.map(
      (action) =>
        ({
          type: action.actionType,
          name: buildPlaybookActionTypeDisplayName(action.actionType),
          tooltip: action.tooltip,
          disabled: action.disabled,
        }) as ActionOptions
    );

    const agentActionAuthenticationMode = actionOptions.find(
      (action) => action.type === ActionTypes.setAgentActionAuthenticationMode
    );

    if (agentActionAuthenticationMode) {
      agentActionAuthenticationMode.name = Labels.commonActions.authenticationMode.label;
    }

    const agentActionConfirmationMode = actionOptions.find(
      (action) => action.type === ActionTypes.setAgentActionConfirmationMode
    );

    if (agentActionConfirmationMode) {
      agentActionConfirmationMode.name = Labels.commonActions.confirmationMode.label;
    }

    const agentActionWebBrowsing = actionOptions.find((action) => action.type === ActionTypes.setAgentWebBrowsing);

    if (agentActionWebBrowsing) {
      agentActionWebBrowsing.name = Labels.commonActions.webBrowsing.label;
    }

    const environmentAuditAction = actionOptions.find((action) => action.type === ActionTypes.setEnvironmentAudit);

    if (environmentAuditAction) {
      environmentAuditAction.name = Labels.commonActions.environmentAudit.label;
    }

    this.actionOptions.set(structuredClone(actionOptions));

    const selection =
      this.playbookAction?.let((currentAction) =>
        this.actionOptions().find((options) => options.type === currentAction)
      ) ?? this.actionOptions()[0];

    this.onActionSelected(selection);
  }

  private _resetActionOnChange() {
    this.selectedAction = undefined;
    this.playbookAction = undefined;
    this.actionProperties = undefined;
  }
}
